import { createContext, useContext, useState, useEffect, type FC, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getStoredCredentials,
  storeCredentials,
  clearCredentials,
  createBasicAuthHeader,
} from '../libs/auth.ts'

interface AuthContextType {
  isAuthenticated: boolean
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
  getAuthHeader: () => string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [credentials, setCredentials] = useState<{ username: string; password: string } | null>(
    null,
  )

  useEffect(() => {
    // Check if credentials are stored on mount
    const stored = getStoredCredentials()
    if (stored) {
      setCredentials(stored)
      setIsAuthenticated(true)
    }
  }, [])

  const login = async (username: string, password: string): Promise<boolean> => {
    // Store credentials
    storeCredentials(username, password)
    setCredentials({ username, password })
    setIsAuthenticated(true)
    return true
  }

  const logout = () => {
    clearCredentials()
    setCredentials(null)
    setIsAuthenticated(false)
  }

  const getAuthHeader = (): string | null => {
    if (!credentials) {
      return null
    }
    return createBasicAuthHeader(credentials.username, credentials.password)
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        getAuthHeader,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
